import React, { useEffect, useMemo, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';

const ECM_METRICS_REPORT_QUERY = gql`
  query EcmMetricsReport($startDate: Date!, $endDate: Date!, $reportRunDate: Date) {
    ecmMetricsReport(startDate: $startDate, endDate: $endDate, reportRunDate: $reportRunDate) {
      meta {
        reportStart
        reportEnd
        reportRunDate
        effectiveReportEnd
        enrolledClients
      }
      definitions {
        touchDefinition
        serviceDefinition
        outreachRule
      }
      metric4Summary {
        option1AvgLoeDaysActiveAsOngoing
        option2ClosedAvgLoeDays
        option2ActiveAvgOngoingDays
        option3ExcludingActiveAvgLoeDays
        closedEnrollments
        activeEnrollments
      }
      metric4Stratification {
        clientsExitedDuringReportingPeriod
        clientsStillEnrolledThroughPeriodEnd
        avgLoeDaysExitedDuringPeriod
        avgOngoingDaysStillEnrolled
      }
      metric4ClientLevel {
        odrparticipant
        enrollmentStart
        enrollmentEnd
        enrollmentStatus
        exitedDuringPeriod
        activeThroughPeriodEnd
        loeDaysOption1ActiveAsOngoing
        loeDaysClosedOnly
      }
      metric5Totals {
        enrolledClientsInPeriod
        touchesTotal
        clientsWithTouch
        avgTouchesPerEnrolledClient
        avgTouchesPerTouchedClientExcludingGhost
        excludedOutreachRows
      }
      metric5Monthly {
        reportMonth
        touches
        clientsWithTouch
      }
      metric6Totals {
        enrolledClientsInPeriod
        reportingMonthsCount
        billableServicesTotal
        clientsWithBillableService
        avgBillableServicesPerEnrolledClientOverPeriod
        avgBillableServicesPerEnrolledClientPerCalendarMonth
        enrolledClientMonthsObserved
        avgBillableServicesPerEnrolledClientMonthObserved
      }
      metric6Monthly {
        reportMonth
        billableServices
        clientsWithBillableService
        activeClientsInMonth
        avgBillableServicesPerActiveClientInMonth
      }
      metric6DrilldownMonthly {
        reportMonth
        inPersonClinical
        inPersonNonclinical
        telehealthClinical
        telehealthNonclinical
        mdtConference
        billableTotal
      }
      metric6RequirementCompliance {
        healthplanGroup
        clientMonths
        clientMonthsMeetingRequirement
        pctClientMonthsMeetingRequirement
      }
      metric65Summary {
        billableServicesTotal
        billableServicesMissingDuration
        billableServicesWithDuration
        avgMinutesPerService
        avgHoursPerService
        totalMinutesDocumented
        totalHoursDocumented
      }
      metric65MonthlyBucket {
        reportMonth
        serviceBucket
        billableServices
        missingDuration
        avgMinutesPerService
        avgHoursPerService
      }
      qaSummary {
        billableServicesOutsideEnrollmentWindow
        excludedOutreachRows
      }
    }
  }
`;

const pad = (value: number) => `${value}`.padStart(2, '0');

const toSqlDate = (value: Date) => {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

const getDefaultStartDate = () => {
  const now = new Date();
  const fiscalYearStart = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(fiscalYearStart, 6, 1);
};

const formatFloat = (value: number | null | undefined, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return Number(value).toFixed(digits);
};

const formatInt = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return Number(value).toLocaleString();
};

const SummaryCard = ({ label, value }: { label: string; value: string }) => {
  return (
    <Card className="p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </Card>
  );
};

export const EcmMetricsReportPage = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());

  const [loadReport, { data, loading, error }] = useLazyQuery(ECM_METRICS_REPORT_QUERY, {
    fetchPolicy: 'network-only',
  });

  const report = data?.ecmMetricsReport;

  const handleGenerate = () => {
    if (!startDate || !endDate) {
      toast.error('Start date and end date are required');
      return;
    }

    const start = toSqlDate(startDate);
    const end = toSqlDate(endDate);

    if (start > end) {
      toast.error('Start date must be on or before end date');
      return;
    }

    loadReport({
      variables: {
        startDate: start,
        endDate: end,
        reportRunDate: toSqlDate(new Date()),
      },
    });
  };

  useEffect(() => {
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasData = useMemo(() => Boolean(report), [report]);

  return (
    <div className="space-y-4 p-6">
      <Card className="space-y-4 p-6">
        <div>
          <h1 className="text-2xl font-semibold">ECM Metrics Report</h1>
          <p className="mt-1 text-sm text-slate-600">
            Metrics 4, 5, 6, and 6.5 from coordinated care data. Outreach is excluded.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
          <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
          <div className="flex items-end">
            <Button className="w-full" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh Report'}
            </Button>
          </div>
        </div>

        {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</div> : null}
      </Card>

      {loading ? <OdrLoadingSpinner /> : null}

      {hasData ? (
        <>
          <Card className="space-y-3 p-4">
            <div className="text-sm font-semibold">Definitions Used</div>
            <div className="text-sm text-slate-700">{report.definitions.touchDefinition}</div>
            <div className="text-sm text-slate-700">{report.definitions.serviceDefinition}</div>
            <div className="text-sm font-medium text-slate-900">{report.definitions.outreachRule}</div>
            <div className="text-xs text-slate-500">
              Window: {report.meta.reportStart} to {report.meta.effectiveReportEnd} (report run date {report.meta.reportRunDate})
            </div>
          </Card>

          <Tabs className="space-y-4" defaultValue="metric4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="metric4">Metric 4 LOE</TabsTrigger>
              <TabsTrigger value="metric5">Metric 5 Touches</TabsTrigger>
              <TabsTrigger value="metric6">Metric 6 Services</TabsTrigger>
              <TabsTrigger value="metric65">Metric 6.5 Duration</TabsTrigger>
              <TabsTrigger value="qa">QA</TabsTrigger>
            </TabsList>

            <TabsContent value="metric4" className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
                <SummaryCard
                  label="Option 1 Avg LOE Days"
                  value={formatFloat(report.metric4Summary.option1AvgLoeDaysActiveAsOngoing)}
                />
                <SummaryCard
                  label="Option 2 Closed Avg"
                  value={formatFloat(report.metric4Summary.option2ClosedAvgLoeDays)}
                />
                <SummaryCard
                  label="Option 2 Active Avg"
                  value={formatFloat(report.metric4Summary.option2ActiveAvgOngoingDays)}
                />
                <SummaryCard
                  label="Option 3 Closed Avg"
                  value={formatFloat(report.metric4Summary.option3ExcludingActiveAvgLoeDays)}
                />
                <SummaryCard label="Closed" value={formatInt(report.metric4Summary.closedEnrollments)} />
                <SummaryCard label="Active" value={formatInt(report.metric4Summary.activeEnrollments)} />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <SummaryCard
                  label="Exited During Period"
                  value={`${formatInt(report.metric4Stratification.clientsExitedDuringReportingPeriod)} (avg ${formatFloat(report.metric4Stratification.avgLoeDaysExitedDuringPeriod)} days)`}
                />
                <SummaryCard
                  label="Still Enrolled Through Period End"
                  value={`${formatInt(report.metric4Stratification.clientsStillEnrolledThroughPeriodEnd)} (avg ${formatFloat(report.metric4Stratification.avgOngoingDaysStillEnrolled)} days)`}
                />
              </div>

              <Card className="p-0">
                <div className="max-h-[520px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ODR</TableHead>
                        <TableHead>Start</TableHead>
                        <TableHead>End</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Exited During Period</TableHead>
                        <TableHead>Active Through End</TableHead>
                        <TableHead>LOE Days (Option 1)</TableHead>
                        <TableHead>LOE Days (Closed)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.metric4ClientLevel.map((row: any) => (
                        <TableRow key={row.odrparticipant}>
                          <TableCell>{row.odrparticipant}</TableCell>
                          <TableCell>{row.enrollmentStart || '-'}</TableCell>
                          <TableCell>{row.enrollmentEnd || '-'}</TableCell>
                          <TableCell>{row.enrollmentStatus}</TableCell>
                          <TableCell>{row.exitedDuringPeriod ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{row.activeThroughPeriodEnd ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{formatInt(row.loeDaysOption1ActiveAsOngoing)}</TableCell>
                          <TableCell>{formatInt(row.loeDaysClosedOnly)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="metric5" className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
                <SummaryCard label="Enrolled Clients" value={formatInt(report.metric5Totals.enrolledClientsInPeriod)} />
                <SummaryCard label="Touches" value={formatInt(report.metric5Totals.touchesTotal)} />
                <SummaryCard label="Clients With Touch" value={formatInt(report.metric5Totals.clientsWithTouch)} />
                <SummaryCard
                  label="Avg Touches / Enrolled Client"
                  value={formatFloat(report.metric5Totals.avgTouchesPerEnrolledClient)}
                />
                <SummaryCard
                  label="Avg Touches / Touched Client"
                  value={formatFloat(report.metric5Totals.avgTouchesPerTouchedClientExcludingGhost)}
                />
                <SummaryCard
                  label="Excluded Outreach Rows"
                  value={formatInt(report.metric5Totals.excludedOutreachRows)}
                />
              </div>

              <Card className="p-0">
                <div className="max-h-[420px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Touches</TableHead>
                        <TableHead>Clients With Touch</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.metric5Monthly.map((row: any) => (
                        <TableRow key={row.reportMonth}>
                          <TableCell>{row.reportMonth}</TableCell>
                          <TableCell>{formatInt(row.touches)}</TableCell>
                          <TableCell>{formatInt(row.clientsWithTouch)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="metric6" className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <SummaryCard label="Billable Services" value={formatInt(report.metric6Totals.billableServicesTotal)} />
                <SummaryCard
                  label="Clients With Billable Service"
                  value={formatInt(report.metric6Totals.clientsWithBillableService)}
                />
                <SummaryCard
                  label="Avg Services / Enrolled Client"
                  value={formatFloat(report.metric6Totals.avgBillableServicesPerEnrolledClientOverPeriod)}
                />
                <SummaryCard
                  label="Avg Services / Enrolled Client Month"
                  value={formatFloat(report.metric6Totals.avgBillableServicesPerEnrolledClientMonthObserved, 3)}
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="p-0">
                  <div className="border-b px-4 py-3 text-sm font-semibold">Monthly Services</div>
                  <div className="max-h-[360px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead>Billable Services</TableHead>
                          <TableHead>Clients With Service</TableHead>
                          <TableHead>Active Clients</TableHead>
                          <TableHead>Avg / Active Client</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.metric6Monthly.map((row: any) => (
                          <TableRow key={row.reportMonth}>
                            <TableCell>{row.reportMonth}</TableCell>
                            <TableCell>{formatInt(row.billableServices)}</TableCell>
                            <TableCell>{formatInt(row.clientsWithBillableService)}</TableCell>
                            <TableCell>{formatInt(row.activeClientsInMonth)}</TableCell>
                            <TableCell>{formatFloat(row.avgBillableServicesPerActiveClientInMonth)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>

                <Card className="p-0">
                  <div className="border-b px-4 py-3 text-sm font-semibold">Health Plan Requirement Compliance</div>
                  <div className="max-h-[360px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Health Plan Group</TableHead>
                          <TableHead>Client Months</TableHead>
                          <TableHead>Meeting Requirement</TableHead>
                          <TableHead>% Meeting</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.metric6RequirementCompliance.map((row: any) => (
                          <TableRow key={row.healthplanGroup}>
                            <TableCell>{row.healthplanGroup}</TableCell>
                            <TableCell>{formatInt(row.clientMonths)}</TableCell>
                            <TableCell>{formatInt(row.clientMonthsMeetingRequirement)}</TableCell>
                            <TableCell>{formatFloat(row.pctClientMonthsMeetingRequirement)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>

              <Card className="p-0">
                <div className="border-b px-4 py-3 text-sm font-semibold">Billable Service Drilldown by Month</div>
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>In-Person Clinical</TableHead>
                        <TableHead>In-Person Nonclinical</TableHead>
                        <TableHead>Telehealth Clinical</TableHead>
                        <TableHead>Telehealth Nonclinical</TableHead>
                        <TableHead>MDT Conference</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.metric6DrilldownMonthly.map((row: any) => (
                        <TableRow key={row.reportMonth}>
                          <TableCell>{row.reportMonth}</TableCell>
                          <TableCell>{formatInt(row.inPersonClinical)}</TableCell>
                          <TableCell>{formatInt(row.inPersonNonclinical)}</TableCell>
                          <TableCell>{formatInt(row.telehealthClinical)}</TableCell>
                          <TableCell>{formatInt(row.telehealthNonclinical)}</TableCell>
                          <TableCell>{formatInt(row.mdtConference)}</TableCell>
                          <TableCell>{formatInt(row.billableTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="metric65" className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
                <SummaryCard label="Billable Services" value={formatInt(report.metric65Summary.billableServicesTotal)} />
                <SummaryCard
                  label="Missing Duration"
                  value={formatInt(report.metric65Summary.billableServicesMissingDuration)}
                />
                <SummaryCard
                  label="With Duration"
                  value={formatInt(report.metric65Summary.billableServicesWithDuration)}
                />
                <SummaryCard label="Avg Minutes / Service" value={formatFloat(report.metric65Summary.avgMinutesPerService)} />
                <SummaryCard label="Avg Hours / Service" value={formatFloat(report.metric65Summary.avgHoursPerService)} />
                <SummaryCard label="Total Hours" value={formatFloat(report.metric65Summary.totalHoursDocumented)} />
              </div>

              <Card className="p-0">
                <div className="border-b px-4 py-3 text-sm font-semibold">Monthly Duration by Service Bucket</div>
                <div className="max-h-[460px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Service Bucket</TableHead>
                        <TableHead>Billable Services</TableHead>
                        <TableHead>Missing Duration</TableHead>
                        <TableHead>Avg Minutes</TableHead>
                        <TableHead>Avg Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.metric65MonthlyBucket.map((row: any, index: number) => (
                        <TableRow key={`${row.reportMonth}-${row.serviceBucket}-${index}`}>
                          <TableCell>{row.reportMonth}</TableCell>
                          <TableCell>{row.serviceBucket}</TableCell>
                          <TableCell>{formatInt(row.billableServices)}</TableCell>
                          <TableCell>{formatInt(row.missingDuration)}</TableCell>
                          <TableCell>{formatFloat(row.avgMinutesPerService)}</TableCell>
                          <TableCell>{formatFloat(row.avgHoursPerService)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="qa" className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <SummaryCard
                  label="Billable Services Outside Enrollment Window"
                  value={formatInt(report.qaSummary.billableServicesOutsideEnrollmentWindow)}
                />
                <SummaryCard
                  label="Excluded Outreach Rows (In Enrollment Window)"
                  value={formatInt(report.qaSummary.excludedOutreachRows)}
                />
              </div>
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
};
